import { z } from 'zod';

import {
  IDENTITY_MODES,
  ATTACHMENT_MAX_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_ALIAS_LENGTH,
  MAX_MESSAGE_LENGTH,
  PRESENCE_PRESETS,
  REQUEST_STATES,
  URGENCY_LEVELS,
  SUPPORTED_ATTACHMENT_MIME_TYPES,
} from './constants';

const trimmedText = (maximum: number) => z.string().trim().min(1).max(maximum);

export const PublicIdSchema = z
  .string()
  .min(8)
  .max(80)
  .regex(/^[a-z]+_[A-Za-z0-9_-]+$/);

export const ProjectIdSchema = z
  .string()
  .min(2)
  .max(48)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const IdentityCreateSchema = z
  .object({
    mode: z.enum(IDENTITY_MODES),
    alias: z.string().trim().max(MAX_ALIAS_LENGTH).optional(),
    generatedAlias: z.string().trim().max(MAX_ALIAS_LENGTH).optional(),
    clientToken: z
      .string()
      .min(43)
      .max(86)
      .regex(/^[A-Za-z0-9_-]+$/),
  })
  .superRefine((value, context) => {
    if (value.mode === 'custom' && !value.alias) {
      context.addIssue({
        code: 'custom',
        message: 'Choose an alias or use anonymous mode.',
        path: ['alias'],
      });
    }
  });

export const DmRequestCreateSchema = z.object({
  message: trimmedText(MAX_MESSAGE_LENGTH),
  intent: z.string().trim().max(80).optional(),
  urgency: z.enum(URGENCY_LEVELS).default('normal'),
  projectId: ProjectIdSchema.default('deffy-direct'),
});

export const PendingMessageCreateSchema = z.object({
  message: trimmedText(MAX_MESSAGE_LENGTH),
  clientMessageId: z.string().uuid(),
});

export const ConversationMessageCreateSchema = PendingMessageCreateSchema;

export const AttachmentInitiateSchema = z
  .object({
    filename: z.string().trim().min(1).max(180),
    declaredMime: z.enum(SUPPORTED_ATTACHMENT_MIME_TYPES),
    sizeBytes: z.number().int().positive(),
    role: z.enum(['attachment', 'voice']).default('attachment'),
    clientAttachmentId: z.string().uuid().optional(),
  })
  .superRefine((value, context) => {
    if (value.sizeBytes > ATTACHMENT_MAX_BYTES[value.declaredMime]) {
      context.addIssue({
        code: 'custom',
        message: 'That file is larger than the limit for its type.',
        path: ['sizeBytes'],
      });
    }
    if (value.role === 'voice' && value.declaredMime !== 'audio/wav') {
      context.addIssue({
        code: 'custom',
        message: 'Voice messages must use the canonical WAV recording format.',
        path: ['declaredMime'],
      });
    }
  });

export const AttachmentBatchSchema = z
  .array(AttachmentInitiateSchema)
  .min(1)
  .max(MAX_ATTACHMENTS_PER_MESSAGE);

export const AdminLoginSchema = z.object({
  password: z.string().min(12).max(512),
});

export const RequestRejectSchema = z.object({
  reasonId: PublicIdSchema,
});

export const BugBasicContextSchema = z
  .object({
    sourceSite: z.url().max(512).nullable().optional(),
    currentUrl: z.url().max(2_048).nullable().optional(),
    projectIdentifier: ProjectIdSchema,
    buildVersion: z.string().trim().max(80).nullable().optional(),
    browserFamily: z.string().trim().max(40),
    operatingSystem: z.string().trim().max(40),
    viewportCategory: z.enum(['phone', 'tablet', 'desktop', 'large-desktop', 'unknown']),
    referrer: z.url().max(2_048).nullable().optional(),
    timestamp: z.iso.datetime(),
  })
  .strict();

export const BugDetailedDiagnosticsSchema = z
  .object({
    systemState: z
      .object({
        online: z.boolean(),
        language: z.string().trim().max(40),
        colorScheme: z.enum(['light', 'dark', 'unknown']),
        reducedMotion: z.boolean(),
        pixelRatioCategory: z.enum(['standard', 'high', 'very-high', 'unknown']),
        navigationType: z.enum(['navigate', 'reload', 'back_forward', 'prerender', 'unknown']),
      })
      .strict()
      .optional(),
    consoleLogs: z.array(z.string().max(1_000)).max(20).optional(),
    recentApplicationEvents: z.array(z.string().max(500)).max(20).optional(),
    networkErrors: z.array(z.string().max(500)).max(10).optional(),
    reproductionTrace: z.array(z.string().max(500)).max(20).optional(),
  })
  .strict();

export const BugReportCreateSchema = z
  .object({
    projectId: ProjectIdSchema,
    description: trimmedText(MAX_MESSAGE_LENGTH),
    expectedBehavior: z.string().trim().max(MAX_MESSAGE_LENGTH).optional(),
    actualBehavior: z.string().trim().max(MAX_MESSAGE_LENGTH).optional(),
    severity: z.enum(['low', 'medium', 'high', 'critical', 'unspecified']).default('unspecified'),
    reproduction: z.string().trim().max(MAX_MESSAGE_LENGTH).optional(),
    replyPreference: z.enum(['none', 'if_needed', 'conversation']).default('none'),
    diagnosticConsent: z.boolean().default(false),
    context: BugBasicContextSchema.optional(),
    diagnostics: BugDetailedDiagnosticsSchema.optional(),
  })
  .superRefine((value, context) => {
    if (value.diagnostics && !value.diagnosticConsent) {
      context.addIssue({
        code: 'custom',
        message: 'Detailed diagnostics require explicit consent.',
        path: ['diagnostics'],
      });
    }
    if (value.context && value.context.projectIdentifier !== value.projectId) {
      context.addIssue({
        code: 'custom',
        message: 'Diagnostic project context must match the selected project.',
        path: ['context', 'projectIdentifier'],
      });
    }
  });

export const BugReportUpdateSchema = z
  .object({
    status: z.enum(['new', 'triaged', 'in_progress', 'resolved', 'closed']).optional(),
    linkedConversationPublicId: PublicIdSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (value) => value.status !== undefined || value.linkedConversationPublicId !== undefined,
    'Provide at least one bug report update.',
  );

export const PresenceUpdateSchema = z.object({
  preset: z.enum(PRESENCE_PRESETS),
  subtitle: z.string().trim().max(160).nullable().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  fallbackPreset: z.enum(PRESENCE_PRESETS).optional(),
  privacyMode: z.boolean().optional(),
});

export const RequestPriorityUpdateSchema = z.object({
  priority: z.number().int().min(-100).max(100),
  pinned: z.boolean().optional(),
  publicEstimatedRank: z.number().int().positive().max(999).nullable().optional(),
});

export const ConversationPauseSchema = z.object({
  paused: z.boolean(),
  reason: z.string().trim().max(160).nullable().optional(),
  expectedReturn: z.string().datetime().nullable().optional(),
});

export const SignalCreateSchema = z.object({
  signal: z.enum(['seen', 'hold_on', 'running_out_of_time', 'patience_running_out']),
});

export const TranscriptErrorReportSchema = z.object({
  note: z.string().trim().max(500).optional(),
});

export const RequestStateSchema = z.enum(REQUEST_STATES);

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});

export const LocalSummarySchema = z.object({
  summary: z.string().trim().min(1).max(800),
  tone: z.string().trim().min(1).max(80),
  intent: z.string().trim().min(1).max(120),
  urgency: z.enum(['casual', 'normal', 'important', 'uncertain']),
});

export type IdentityCreateInput = z.infer<typeof IdentityCreateSchema>;
export type DmRequestCreateInput = z.infer<typeof DmRequestCreateSchema>;
export type PendingMessageCreateInput = z.infer<typeof PendingMessageCreateSchema>;
export type ConversationMessageCreateInput = z.infer<typeof ConversationMessageCreateSchema>;
export type AttachmentInitiateInput = z.infer<typeof AttachmentInitiateSchema>;
export type AdminLoginInput = z.infer<typeof AdminLoginSchema>;
export type RequestRejectInput = z.infer<typeof RequestRejectSchema>;
export type BugReportCreateInput = z.infer<typeof BugReportCreateSchema>;
export type BugBasicContext = z.infer<typeof BugBasicContextSchema>;
export type BugDetailedDiagnostics = z.infer<typeof BugDetailedDiagnosticsSchema>;
export type BugReportUpdateInput = z.infer<typeof BugReportUpdateSchema>;
export type PresenceUpdateInput = z.infer<typeof PresenceUpdateSchema>;
export type RequestPriorityUpdateInput = z.infer<typeof RequestPriorityUpdateSchema>;
export type ConversationPauseInput = z.infer<typeof ConversationPauseSchema>;
export type SignalCreateInput = z.infer<typeof SignalCreateSchema>;
export type TranscriptErrorReportInput = z.infer<typeof TranscriptErrorReportSchema>;
export type LocalSummary = z.infer<typeof LocalSummarySchema>;
